import { useEffect, useRef } from 'react';
import { TabletopState, SelectedItem, TabletopItemHandler } from '../types';
import { CardStackDirection, getStackedCardPositions } from '../utils/cardStacking';

interface UseKeyboardShortcutsProps {
  selectedItem: SelectedItem | null;
  selectedCardIds: string[];
  tabletopState: TabletopState;
  setTabletopState: React.Dispatch<React.SetStateAction<TabletopState>>;
  clearSelection: () => void;
  setZoomedCard: React.Dispatch<React.SetStateAction<string | null>>;
  zoomedCard: string | null;
  hoveredTableCardId?: string | null;
  itemHandlers: Record<string, TabletopItemHandler>;
  moveCardGroup: (cardIds: string[], positions: Record<string, { x: number; y: number }>) => void;
}

export const useKeyboardShortcuts = ({
  selectedItem,
  selectedCardIds,
  tabletopState,
  setTabletopState,
  clearSelection,
  setZoomedCard,
  zoomedCard,
  hoveredTableCardId,
  itemHandlers,
  moveCardGroup
}: UseKeyboardShortcutsProps) => {
  const lastPointerRef = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const handlePointerMove = (event: PointerEvent) => {
      lastPointerRef.current = { x: event.clientX, y: event.clientY };
    };

    window.addEventListener('pointermove', handlePointerMove, { passive: true });
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
    };
  }, []);

  useEffect(() => {
    const getTableCardUnderPointer = () => {
      const pointer = lastPointerRef.current;
      if (!pointer) {
        return null;
      }

      const element = document.elementFromPoint(pointer.x, pointer.y);
      const cardElement = element?.closest('[data-table-card-id]');
      const cardId = cardElement?.getAttribute('data-table-card-id') ?? null;
      return cardId && tabletopState.cards[cardId] ? cardId : null;
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented) {
        return;
      }

      // Prevent handling if typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }
      
      // Handle global shortcuts
      switch (e.key) {
        case 'Escape':
          // Close any modals or zoomed cards and clear selection
          setZoomedCard(null);
          clearSelection();
          return;
      }

      if (e.key.toLowerCase() === 'z' && zoomedCard) {
        e.preventDefault();
        setZoomedCard(null);
        return;
      }

      const targetTableCardId = hoveredTableCardId && tabletopState.cards[hoveredTableCardId]
        ? hoveredTableCardId
        : getTableCardUnderPointer() ?? (
          selectedItem?.type === 'card' && tabletopState.cards[selectedItem.id] ? selectedItem.id : null
        );

      if (e.key.toLowerCase() === 'z' && targetTableCardId) {
        e.preventDefault();
        setZoomedCard(prevId => (prevId === targetTableCardId ? null : targetTableCardId));
        return;
      }

      if (e.key.toLowerCase() === 'f' && targetTableCardId) {
        const handler = itemHandlers.card;
        if (!handler) {
          return;
        }

        const shouldFlipSelectedGroup =
          selectedItem?.type === 'card' &&
          selectedCardIds.length > 1 &&
          selectedCardIds.includes(targetTableCardId);

        if (shouldFlipSelectedGroup) {
          selectedCardIds.forEach(cardId => {
            handler.handleKeyboardShortcut(cardId, 'f', tabletopState);
          });
        } else {
          handler.handleKeyboardShortcut(targetTableCardId, 'f', tabletopState);
        }

        e.preventDefault();
        return;
      }
      
      // If we have a selected item, handle type-specific shortcuts
      if (selectedItem) {
        const handler = itemHandlers[selectedItem.type];
        
        if (handler) {
          const key = e.key.toLowerCase();

          if (selectedItem.type === 'card' && selectedCardIds.length > 1) {
            const directionalStackKeys: Record<string, CardStackDirection> = {
              arrowright: 'right',
              arrowdown: 'down',
              arrowleft: 'left',
              arrowup: 'up'
            };
            const stackDirection =
              key === 's'
                ? 'compact'
                : e.altKey
                  ? directionalStackKeys[key]
                  : undefined;

            if (stackDirection) {
              const layout = getStackedCardPositions(selectedCardIds, tabletopState.cards, stackDirection);
              if (layout.cardIds.length > 1) {
                moveCardGroup(layout.cardIds, layout.positions);
              }
              e.preventDefault();
              return;
            }
          }

          if (selectedItem.type === 'card' && selectedCardIds.length > 1 && key === 'f') {
            selectedCardIds.forEach(cardId => {
              handler.handleKeyboardShortcut(cardId, key, tabletopState);
            });
            e.preventDefault();
            return;
          }

          const newState = handler.handleKeyboardShortcut(
            selectedItem.id,
            key,
            tabletopState
          );
          
          // If a state update was returned, apply it
          if (newState) {
            setTabletopState(newState);
          }

          if (key === 'delete') {
            clearSelection();
          }
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    selectedItem, 
    selectedCardIds,
    tabletopState, 
    setTabletopState,
    clearSelection,
    setZoomedCard,
    zoomedCard,
    hoveredTableCardId,
    itemHandlers,
    moveCardGroup
  ]);
}; 
